import type { TransactionHistory } from '@/types/Transactions'

export type Status = 'pending' | 'active' | 'deactivated' | 'waiting' | 'terminated' | 'expired' | ''

export const useProducts = defineStore('products', {
	state: () => {
		const thColumnClass = '!bg-gray-100 !px-[10px] text-xs !text-gray-600'
		const style = {
			customHeaderCell: () => ({
				class: `!rounded-tl-lg ${thColumnClass}`
			}),
			customCell: () => ({
				class: 'text-xs'
			})
		}
		const planUsageColumn = [
			{
				key: 'claim_made',
				dataIndex: 'claim_made',
				title: 'Claim Made',
				...style
			},
			{
				key: 'claim_paid',
				dataIndex: 'claim_paid',
				title: 'Claim Paid',
				...style
			},
			{
				key: 'details',
				dataIndex: 'details',
				title: 'Details',
				...style
			}
		]
		const autoPolicyColumn = [
			{
				key: 'sn',
				dataIndex: 'sn',
				title: 'S/N',
				align: 'center'
			},
			{
				key: 'vehicle_type',
				dataIndex: 'vehicle_type',
				title: 'Vehicle Type'
			},
			{
				key: 'manufacturer',
				dataIndex: 'manufacturer',
				title: 'Manufacturer'
			},
			{
				key: 'plate_number',
				dataIndex: 'plate_number',
				title: 'Plate Number'
			},
			{
				key: 'value',
				dataIndex: 'value',
				title: 'Value'
			},
			{
				key: 'custodian',
				dataIndex: 'custodian',
				title: 'Custodian'
			},
			{
				key: 'status',
				dataIndex: 'status',
				title: 'Status'
			},
			{
				key: 'details',
				dataIndex: 'details',
				title: 'Details'
			}
		]
		const comprehensiveColumn = [...autoPolicyColumn]
		comprehensiveColumn.splice(2, 0, {
			key: 'model',
			dataIndex: 'model',
			title: 'Model'
		})

		return {
			vehicle_manufacturers: [] as SelectOptionContext,
			cover_period: [
				// { value: 'monthly', label: 'Monthly', id: 1 },
				{ value: 'quarterly', label: 'Quarterly', id: 3 },
				{ value: 'bi-annual', label: 'Bi-Annual', id: 6 },
				{ value: 'annual', label: 'Annual', id: 12 }
			] as SelectOptionContext,
			vehicle_types: [],
			transactions: [] as Array<TransactionHistory>,
			claims: {
				gadget: [
					{
						key: 'sn',
						dataIndex: 'sn',
						title: 'S/N'
					},
					{
						key: 'gadget_type',
						dataIndex: 'gadget_type',
						title: 'Gadget Type'
					},
					{
						key: 'brand',
						dataIndex: 'brand',
						title: 'Brand'
					},
					{
						key: 'model',
						dataIndex: 'model',
						title: 'Model'
					},
					{
						key: 'serial_number',
						dataIndex: 'serial_number',
						title: 'Serial Number'
					},
					{
						key: 'value',
						dataIndex: 'value',
						title: 'Value'
					},
					{
						key: 'custodian',
						dataIndex: 'custodian',
						title: 'Custodian'
					},

					{
						key: 'details',
						dataIndex: 'details',
						title: 'Gadget'
					}
				],
				auto: [
					{
						dataIndex: 'sn',
						key: 'sn',
						title: 'S/N'
					},
					{
						dataIndex: 'vehicle_type',
						key: 'vehicle_type',
						title: 'Vehicle Type'
					},
					{
						key: 'manufacturer',
						dataIndex: 'manufacturer',
						title: 'Manufacturer'
					},
					{
						dataIndex: 'model',
						key: 'model',
						title: 'Model'
					},
					{
						key: 'plate_number',
						dataIndex: 'plate_number',
						title: 'Plate Number'
					},
					{
						dataIndex: 'value',
						key: 'value',
						title: 'Value'
					},
					{
						dataIndex: 'custodian',
						key: 'custodian',
						title: 'Driver'
					},
					{
						key: 'details',
						dataIndex: 'details',
						title: 'Vehicle'
					}
				]
			} as Record<'gadget' | 'auto', TableColumn[]>,
			planUsageColumns: {
				gadget: [
					{
						key: 'sn',
						dataIndex: 'sn',
						title: 'S/N',
						...style
					},
					{
						key: 'gadget_type',
						dataIndex: 'gadget_type',
						title: 'Gadget Type',
						...style
					},
					{
						key: 'gadget_model',
						dataIndex: 'gadget_model',
						title: 'Gadget Model',
						...style
					},
					...planUsageColumn
				] as TableColumn[],
				auto: [
					{
						key: 'sn',
						dataIndex: 'sn',
						title: 'S/N',
						...style
					},
					{
						key: 'vehicle_type',
						dataIndex: 'vehicle_type',
						title: 'Vehicle Type',
						...style
					},
					{
						key: 'vehicle_model',
						dataIndex: 'vehicle_model',
						title: 'Vehicle Model',
						...style
					},
					...planUsageColumn
				] as TableColumn[],
				health: [
					{
						key: 'sn',
						dataIndex: 'sn',
						title: 'S/N',
						...style
					},
					{
						key: 'staff_name',
						dataIndex: 'staff_name',
						title: 'Staff Full Name',
						...style
					},
					{
						key: 'amount_used',
						dataIndex: 'amount_used',
						title: 'Amount used',
						...style
					},
					{
						key: 'details',
						dataIndex: 'details',
						title: 'Details',
						...style
					}
				] as TableColumn[],
				travel: [
					{
						key: 'sn',
						dataIndex: 'sn',
						title: 'S/N',
						...style
					},
					{
						key: 'destination',
						dataIndex: 'destination',
						title: 'Destination',
						...style
					},
					{
						key: 'staff_name',
						dataIndex: 'staff_name',
						title: 'Staff Full Name',
						...style
					},
					...planUsageColumn
				] as TableColumn[]
			},
			policiesTableColumn: {
				health: [
					{
						key: 'sn',
						dataIndex: 'sn',
						title: 'S/N'
					},
					{
						key: 'first_name',
						dataIndex: 'first_name',
						title: 'First Name'
					},
					{
						key: 'last_name',
						dataIndex: 'last_name',
						title: 'Last Name'
					},
					{
						key: 'email',
						dataIndex: 'email',
						title: 'Email Address'
					},
					{
						key: 'phone_number',
						dataIndex: 'phone_number',
						title: 'Phone Number',
						className: 'whitespace-nowrap'
					},
					{
						key: 'dependants',
						dataIndex: 'dependants',
						title: 'No. of dependants'
					},
					{
						key: 'status',
						dataIndex: 'status',
						title: 'Status'
					},
					{
						key: 'details',
						dataIndex: 'details',
						title: 'Details'
					}
				] as TableColumn[],
				gadget: [
					{
						key: 'sn',
						dataIndex: 'sn',
						title: 'S/N'
					},
					{
						key: 'gadget_type',
						dataIndex: 'gadget_type',
						title: 'Gadget Type'
					},
					{
						key: 'gadget_brand',
						dataIndex: 'brand',
						title: 'Brand'
					},
					{
						key: 'gadget_model',
						dataIndex: 'model',
						title: 'Model'
					},
					{
						key: 'serial_number',
						dataIndex: 'serial_number',
						title: 'Serial Number'
					},
					{
						key: 'gadget_value',
						dataIndex: 'value',
						title: 'Value'
					},
					{
						key: 'custodian',
						dataIndex: 'custodian',
						title: 'Custodian'
					},
					{
						key: 'status',
						dataIndex: 'status',
						title: 'Status'
					},
					{
						key: 'details',
						dataIndex: 'details',
						title: 'Details'
					}
				] as TableColumn[],
				travel: [
					{
						key: 'sn',
						dataIndex: 'sn',
						title: 'S/N'
					},
					{
						key: 'location_name',
						dataIndex: 'location_name',
						title: 'Location'
					},
					{
						key: 'start_date',
						dataIndex: 'start_date',
						title: 'Travel Date'
					},
					{
						key: 'end_date',
						dataIndex: 'end_date',
						title: 'Return Date'
					},

					{
						key: 'first_name',
						dataIndex: 'first_name',
						title: 'First Name'
					},
					{
						key: 'last_name',
						dataIndex: 'last_name',
						title: 'Last Name'
					},
					{
						key: 'email',
						dataIndex: 'email',
						title: 'Email Address'
					},
					{
						key: 'phone_number',
						dataIndex: 'phone_number',
						title: 'Phone Number'
					},
					{
						key: 'status',
						dataIndex: 'status',
						title: 'Status'
					},
					{
						key: 'details',
						dataIndex: 'details',
						title: 'Details'
					}
				],
				auto: autoPolicyColumn as TableColumn[],
				comprehensive: comprehensiveColumn as TableColumn[]
			}
		}
	},
	getters: {
		ClaimsRecordColumn(state) {
			return state.claims
		},
		Transactions(state) {
			return state.transactions
		},

		CoverPeriods(state) {
			return state.cover_period
		},
		VehicleMake(state) {
			return state.vehicle_manufacturers
		}
	},
	actions: {
		setCoverPeriod(payload: SelectOptionContext) {
			if (payload) {
				this.cover_period = payload
			}
		},
		setVehicleMake(payload: SelectOptionContext) {
			this.vehicle_manufacturers = payload
		}
	}
})

export * from './benefits'
